import { useCallback, useMemo } from 'react';
import { faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import { Box, Typography, FormLabel, TextField, FormControl } from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import Icon from '@/components/general/Icon';
import { AnswerTypes, QuestionOption } from '@/types/api';
import { getComparator } from '@/utils/order';
import styles from './UnansweredQuestion.styles';

type UnansweredQuestionProps = {
  label: string;
  answerType: AnswerTypes;
  id: string;
  answer: string | string[];
  addOneMoreLine: (index: string) => void;
  removeOneLine: (index: number, id: string) => void;
  onAnswerHandler: (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    id: string,
    index?: number
  ) => void;
  options?: QuestionOption[];
  description?: string;
  isRequired?: boolean;
  disabled?: boolean;
  isFormSubmitted?: boolean;
};

const UnansweredQuestion = function ({
  label,
  description,
  answerType,
  options,
  id,
  answer,
  isRequired,
  disabled = false,
  isFormSubmitted,
  addOneMoreLine,
  removeOneLine,
  onAnswerHandler,
}: UnansweredQuestionProps): JSX.Element {
  const showIsRequiredMessage = useMemo(() => {
    const isArrayOrPriorities =
      answerType === AnswerTypes.ARRAY || answerType === AnswerTypes.PRIORITY;
    if (!isRequired || !isFormSubmitted) return false;

    if (isArrayOrPriorities && typeof answer !== 'string') {
      return !answer.filter(Boolean).length;
    }

    return !answer;
  }, [answerType, answer, isRequired, isFormSubmitted]);

  const inputHelperText = useMemo(() => {
    if (showIsRequiredMessage) return 'This field is required';

    return undefined;
  }, [showIsRequiredMessage]);

  const MultipleAnswerFields = useCallback(
    (value: string, index: number): JSX.Element => {
      const isLastItemWithAnAnswer = index + 1 === answer.length && !!value;
      const hasMoreThanOneAnswer = answer.length !== 1;
      const allLinesHaveAnAnswer =
        typeof answer !== 'string' && !answer.every((answerValue) => answerValue);

      return (
        <div
          data-testid={`unanswered-question-array-${index + 1}`}
          key={index}
          style={styles.answersGroup}
        >
          <TextField
            label="Enter your answer"
            fullWidth
            size="small"
            multiline
            type="text"
            onChange={(e) => onAnswerHandler(e, id, index)}
            value={value}
            disabled={disabled}
            error={showIsRequiredMessage}
            helperText={inputHelperText}
          />
          {isLastItemWithAnAnswer && (
            <IconButton
              color="primary"
              data-testid={`unanswered-question-array-add-line-${index + 1}`}
              disabled={allLinesHaveAnAnswer || disabled}
              aria-label="Add one line"
              onClick={() => addOneMoreLine(id)}
            >
              <Icon icon={faPlus} color="primary" variantColor="dark" size={20} />
            </IconButton>
          )}
          {hasMoreThanOneAnswer && (
            <IconButton
              color="primary"
              aria-label="Remove line"
              disabled={disabled}
              data-testid={`unanswered-question-array-remove-line-${index + 1}`}
              onClick={() => removeOneLine(index, id)}
            >
              <Icon icon={faTrash} color="error" variantColor="dark" size={20} />
            </IconButton>
          )}
        </div>
      );
    },
    [
      addOneMoreLine,
      answer,
      disabled,
      id,
      inputHelperText,
      onAnswerHandler,
      removeOneLine,
      showIsRequiredMessage,
    ]
  );

  const NumberAnswerField = useCallback(
    (option: QuestionOption, index): JSX.Element => {
      const hasMoreThanFiveOptions = options && options?.length > 5;
      const labelStyles = {
        ...styles.inputRadioOption,
        ...(answer === option.label && styles.inputRadioOptionChecked),
        ...(hasMoreThanFiveOptions && {
          ...styles.squareInputRadioOption,
          width: `${100 / (options?.length ?? 1)}%`,
        }),
      };

      return (
        <FormLabel
          data-testid={`unanswered-question-number-${index + 1}`}
          key={option.id}
          sx={labelStyles}
          htmlFor={`question-${option.id}`}
          disabled={disabled}
          error={showIsRequiredMessage}
        >
          <input
            disabled={disabled}
            onChange={(e) => onAnswerHandler(e, id)}
            type="radio"
            checked={answer === option.label}
            id={`question-${option.id}`}
            value={option.label}
            name={`question-${option.id}`}
          />
          {option.label}
        </FormLabel>
      );
    },
    [answer, disabled, id, onAnswerHandler, options, showIsRequiredMessage]
  );

  const renderAnswers = useMemo(() => {
    const isArrayOrPriotityType =
      answerType === AnswerTypes.ARRAY || answerType === AnswerTypes.PRIORITY;

    if (isArrayOrPriotityType && !!answer?.length && typeof answer !== 'string') {
      return answer.map(MultipleAnswerFields);
    }

    if (answerType === AnswerTypes.NUMBER && !!options?.length) {
      const sortedByOptionValue = options
        .map((option) => ({
          ...option,
          value: parseInt(option.label, 10),
        }))
        .sort(getComparator('asc', 'value'));

      return (
        <FormControl
          sx={styles.numberInputContainer}
          error={showIsRequiredMessage}
          variant="outlined"
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-around',
            }}
          >
            {sortedByOptionValue.map(NumberAnswerField)}
          </Box>
          {showIsRequiredMessage && <FormHelperText error>{inputHelperText}</FormHelperText>}
        </FormControl>
      );
    }

    return (
      <TextField
        data-testid="unanswered-question-string"
        label="Enter your answer"
        fullWidth
        size="small"
        multiline
        type="text"
        onChange={(e) => onAnswerHandler(e, id)}
        value={answer}
        disabled={disabled}
        error={showIsRequiredMessage}
        helperText={inputHelperText}
      />
    );
  }, [
    answerType,
    answer,
    options,
    disabled,
    showIsRequiredMessage,
    inputHelperText,
    MultipleAnswerFields,
    NumberAnswerField,
    onAnswerHandler,
    id,
  ]);

  return (
    <Box data-testid="unanswered-question-component" sx={styles.container}>
      <Box marginBottom={description ? 0 : 1}>
        <Typography
          variant="h6"
          data-testid="unanswered-question-label"
          color="primary"
          component="span"
          fontWeight={500}
        >
          {label}{' '}
        </Typography>

        {!isRequired && (
          <Typography component="span" color="primary.light">
            (optional)
          </Typography>
        )}
      </Box>

      {description && (
        <Typography marginBottom={1} variant="subtitle1">
          {description}
        </Typography>
      )}

      <Box
        display="flex"
        data-testid="unanswered-question-input-box"
        sx={{
          ...styles.input,
          ...(answerType === AnswerTypes.NUMBER ? styles.inputRadio : {}),
        }}
      >
        {renderAnswers}
      </Box>
    </Box>
  );
};

export default UnansweredQuestion;
